import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { ShieldCheck, Building2, Wrench, Shirt } from "lucide-react";
import AdminEntitySettings from "./AdminEntitySettings";
import AdminMaintenance from "./AdminMaintenance";
import AdminLaundryPricing from "./AdminLaundryPricing";

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState("entities");
  const [cgpText, setCgpText] = useState("");
  const [cgpLoading, setCgpLoading] = useState(true);
  const [cgpSaving, setCgpSaving] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [confirmCleanup, setConfirmCleanup] = useState(false);

  useEffect(() => {
    const loadCgp = async () => {
      try {
        const res = await api.get("/admin/cgp");
        setCgpText(res.data.text || "");
      } catch (e) {
        console.error("Failed to load CGP");
      } finally {
        setCgpLoading(false);
      }
    };
    loadCgp();
  }, []);

  const saveCgp = async () => {
    try {
      setCgpSaving(true);
      await api.put("/admin/cgp", { text: cgpText });
      toast({ title: "CGP sauvegardées", description: "Les Conditions Générales ont été mises à jour." });
    } catch (e) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder les CGP.", variant: "destructive" });
    } finally {
      setCgpSaving(false);
    }
  };

  const previewCleanup = async () => {
    try {
      setCleanupLoading(true);
      const res = await api.post("/admin/database/cleanup", null, { params: { confirm: false } });
      setCleanupPreview(res.data.to_delete);
      setConfirmCleanup(true);
    } catch (e) {
      toast({ title: "Erreur", description: "Impossible de prévisualiser le nettoyage.", variant: "destructive" });
    } finally {
      setCleanupLoading(false);
    }
  };

  const executeCleanup = async () => {
    try {
      setCleanupLoading(true);
      const res = await api.post("/admin/database/cleanup", null, { params: { confirm: true } });
      toast({ title: "Base nettoyée", description: `${res.data.deleted.owners} propriétaires, ${res.data.deleted.properties} logements, ${res.data.deleted.stays} séjours supprimés.` });
      setCleanupPreview(null);
      setConfirmCleanup(false);
      window.location.reload();
    } catch (e) {
      toast({ title: "Erreur", description: "Échec du nettoyage de la base.", variant: "destructive" });
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        <button
          data-testid="settings-tab-entities"
          onClick={() => setActiveTab("entities")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "entities" ? "border-violet-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
        >
          <Building2 className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          Entités émettrices
        </button>
        <button
          data-testid="settings-tab-cgp"
          onClick={() => setActiveTab("cgp")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "cgp" ? "border-violet-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
        >
          <ShieldCheck className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          CGP
        </button>
        <button
          data-testid="settings-tab-maintenance"
          onClick={() => setActiveTab("maintenance")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "maintenance" ? "border-violet-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
        >
          <Wrench className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          Maintenance
        </button>
        <button
          data-testid="settings-tab-laundry"
          onClick={() => setActiveTab("laundry")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "laundry" ? "border-violet-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
        >
          <Shirt className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          Blanchisserie
        </button>
        <button
          data-testid="settings-tab-cleanup"
          onClick={() => setActiveTab("cleanup")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "cleanup" ? "border-red-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
        >
          ⚠️ Nettoyage
        </button>
      </div>

      {activeTab === "entities" && <AdminEntitySettings />}

      {activeTab === "maintenance" && <AdminMaintenance />}

      {activeTab === "laundry" && <AdminLaundryPricing />}

      {activeTab === "cgp" && (
      <div className="rounded-xl border border-white/10 bg-white/[0.08] p-6">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-violet-400" />
          Conditions Générales de Prestation (CGP)
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Les propriétaires doivent accepter ces conditions lors de leur première connexion. Modifiez le texte ci-dessous si nécessaire.
        </p>
        {cgpLoading ? (
          <div className="text-slate-400 py-8 text-center">Chargement...</div>
        ) : (
          <>
            <Textarea data-testid="cgp-textarea" value={cgpText} onChange={(e) => setCgpText(e.target.value)} className="min-h-[300px] bg-slate-950 border-slate-700 text-slate-200 font-mono text-xs" placeholder="Entrez les CGP..." />
            <div className="mt-4 flex justify-end">
              <Button data-testid="cgp-save-btn" onClick={saveCgp} disabled={cgpSaving} className="bg-violet-600 hover:bg-violet-500">
                {cgpSaving ? "Sauvegarde..." : "Sauvegarder les CGP"}
              </Button>
            </div>
          </>
        )}
      </div>
      )}

      {activeTab === "cleanup" && (
      <div className="rounded-xl border border-red-900/50 bg-red-950/20 p-6">
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          ⚠️ Nettoyage de la base de données
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Supprime tous les propriétaires, logements, séjours et données associées. 
          <strong className="text-red-400"> Cette action est irréversible.</strong>
          <br />Le compte administrateur sera conservé.
        </p>
        {cleanupPreview && confirmCleanup ? (
          <div className="space-y-4">
            <div className="bg-slate-900 rounded-lg p-4 border border-red-800">
              <h4 className="font-medium text-red-400 mb-2">Données à supprimer :</h4>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>• {cleanupPreview.owners} propriétaires</li>
                <li>• {cleanupPreview.properties} logements</li>
                <li>• {cleanupPreview.stays} séjours</li>
                <li>• {cleanupPreview.invoices} factures</li>
                <li>• {cleanupPreview.surcharges} surcoûts</li>
                <li>• {cleanupPreview.interventions} interventions</li>
                <li>• {cleanupPreview.users} utilisateurs (hors admin)</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setCleanupPreview(null); setConfirmCleanup(false); }} className="border-slate-600">Annuler</Button>
              <Button data-testid="cleanup-confirm-btn" onClick={executeCleanup} disabled={cleanupLoading} className="bg-red-600 hover:bg-red-500">
                {cleanupLoading ? "Suppression..." : "Confirmer la suppression"}
              </Button>
            </div>
          </div>
        ) : (
          <Button data-testid="cleanup-preview-btn" onClick={previewCleanup} disabled={cleanupLoading} variant="outline" className="border-red-700 text-red-400 hover:bg-red-950">
            {cleanupLoading ? "Chargement..." : "Prévisualiser le nettoyage"}
          </Button>
        )}
      </div>
      )}
    </div>
  );
}
